import { useEffect, useState } from "react";
import PageWrapper from "../../components/common/PageWrapper";
import Header from "../../components/common/Header";
import BodyWrapper from "../../components/common/BodyWrapper";
import Footer from "../../components/common/Footer";
import Wrapper from "../../components/dashboard/dashboardPartials/Wrapper";
import axios from "../../config/axios";
import { useGeneral } from "../../components/contexts/GeneralContext";
import { useNavigate } from "react-router-dom";
import NoItemsFound from "../../components/common/NoItemsFound";
import ApiErrorMessage from "../../components/common/ApiErrorMessage";
import FullScreenLoading from "../../components/common/loading/FullScreenLoading";
import GeneralButton from "../../components/common/GeneralButton";
import { motion } from "framer-motion";

const useScrollToTop = () => {
  useEffect(() => {
    const timer = setTimeout(() => {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }, 100);
    return () => clearTimeout(timer);
  }, []);
};

const NotificationsPage = () => {
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [content, setContent] = useState({ items: [], pagination: {} });
  const [error, setError] = useState("");
  const { discountCoupons } = useGeneral();
  const fetchPayments = async (pageNum = 1) => {
    try {
      setLoading(true);

      const params = { page: pageNum };

      const { data } = await axios.get("/dashboard/notifications", { params });

      setContent((prev) => ({
        items: pageNum === 1 ? data.items : [...prev.items, ...data.items],
        pagination: data.pagination,
      }));

      setError("");
    } catch (err) {
      console.error(err);
      setError("Failed to load payments. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  // Initial load and when filters change
  useEffect(() => {
    fetchPayments(1);
    setPage(1);
  }, []);

  const handleNext = () => {
    if (content?.pagination?.pageCount && page < content.pagination.pageCount) {
      const nextPage = page + 1;
      setPage(nextPage);
      fetchPayments(nextPage);
    }
  };
  useScrollToTop();
  const navigate = useNavigate();
  return (
    <PageWrapper loading={false} className="bg-bg min-h-screen">
      <Header code={discountCoupons} />
      {loading && page === 1 && <FullScreenLoading />}
      <BodyWrapper className="mb-0 xl:mb-0">
        <Wrapper goBack={true}
          component={
            !loading && error ? (
              <ApiErrorMessage
                message1="Failed to load notifications"
                message2="Please check your connection and try again."
              />
            ) : !loading && !content?.items?.length ? (
              <NoItemsFound
                message1="No Notifications Found"
                message2={"Try again later."}
              />
            ) : (
              <motion.div
                    initial={{ opacity: 0, y: -30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1, duration: 0.5, ease: "easeOut" }} className="">
                <div className="mt-6 w-full bg-[#DEE2DD]/40 rounded-[15px] px-6 pt-6 pb-8 lg:px-12 overflow-auto">
                  <h3 className="text-28pt pb-2 mb-2">Notifications</h3>
                  {/* Notification List */}
                  {content?.items?.map((note) => (
                    <li
                      key={note._id}
                      title=" Click to view notification details"
                      onClick={() =>
                        navigate(`/account/notifications/${note._id}`)
                      }
                      className={`flex items-start mb-4 p-4 rounded-[10px] relative transition-all hover:shadow-md cursor-pointer ${
                        note.isSeenByReceiver === "1"
                          ? "bg-[#fff]/80 border border-gray "
                          : "bg-[#fff] border border-[#A0AE3F]"
                      }`}
                    >
                      {/* Icon */}
                      {note.isSeenByReceiver === "0" && (
                        <div className="mt-3 me-3 w-3 h-3 rounded-full bg-orange ripple"></div>
                      )}

                      {/* Content */}
                      <div className="flex-1 flex flex-col gap-1 py-2">
                        <div className="flex items-center gap-2 absolute right-2 top-1">
                          {/* Status Badge */}

                          <p className="text-14pt text-[#808080] font-mono">
                            {note.createdAt}
                          </p>
                        </div>

                        {/* Description */}
                        <p className="text-24pt mb-1">{note.message}</p>

                        {/* Category Badge */}
                        <p className="text-14pt mt-0.5 inline-flex items-center gap-2">
                          <span
                            className={`px-3 py-0.5 rounded-full text-12pt font-mono ${
                              note.importance === "Medium"
                                ? "bg-green/10 text-green border border-green/30"
                                : note.importance === "High"
                                ? "bg-orange/10 text-orange border border-orange/30"
                                : note.importance === "Low"
                                ? "bg-secondary/10 text-secondary border border-secondary/30"
                                : "bg-secondary/10 text-secondary border border-secondary/30"
                            }`}
                          >
                            {note.importance}
                          </span>
                        </p>
                      </div>
                    </li>
                  ))}
                  {content.pagination?.pageCount > 1 &&
                    page < content.pagination.pageCount && (
                      <div className="col-span-full flex justify-center w-full mt-2 xl:mt-4">
                        <GeneralButton
                          text={loading ? "Loading..." : "Load More"}
                          title="Click to load more items"
                          onClick={handleNext}
                          disabled={loading}
                          bgColor="#D06F36"
                        />
                      </div>
                    )}

                  {loading && page > 1 && (
                    <div className="col-span-full flex justify-center mt-3">
                      <div className="w-5 h-5 border-4 border-tertiary border-t-transparent rounded-full animate-spin"></div>
                    </div>
                  )}
                </div>
              </motion.div>
            )
          }
        />
      </BodyWrapper>

      <Footer />
    </PageWrapper>
  );
};

export default NotificationsPage;
